/**
 * /ndt-inspection/instruments/:id — one NDT instrument: its specs, its
 * calibration history, and the writes on it (GET / PATCH / DELETE
 * /ndt-inspection/instruments/:id, POST /instruments/:id/calibrations).
 *
 * The id is the EQUIPMENT row's. Detach removes only the NDT specs; the unit
 * and its calibration history stay in Equipment. Calibration is append-only —
 * a correction is a new row. Status, due date and days-until-due are the API's.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarCheck, Clock, Gauge, Pencil, Plus, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDetachNdtInstrument, useNdtInstrument } from '../../hooks/useNdtInspection';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, canCalibrateNdt, daysHint, ndtCalibrationSkin, ndtErrorMessage, ndtKindLabel } from '../../lib/ndt-inspection';
import { CalibrationDialog, ConfirmDialog, EditSpecsDialog } from './NdtDialogs';
import { isNotFound, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtLoading, NdtPanel, NdtSpec, ndtDay } from './NdtBits';

export default function NdtInstrumentDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useNdtInstrument(id);
  const detach = useDetachNdtInstrument();
  const [editing, setEditing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [detaching, setDetaching] = useState(false);
  const [detachError, setDetachError] = useState<string | null>(null);

  const instrument = query.data?.data ?? null;
  const meta = query.data?.meta;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const canCalibrate = canCalibrateNdt(user?.role) && !!instrument;
  const canEdit = canAdminNdt(user?.role) && !!instrument;
  const status = instrument?.calibrationStatus;
  const calibrations = instrument?.calibrations ?? [];

  return (
    <div data-testid="ndt-instrument-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection · Instrument"
        title={instrument?.name || (query.isLoading ? 'Instrument' : '—')}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canCalibrate ? (
            <button type="button" onClick={() => setRecording(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Log calibration
            </button>
          ) : undefined
        }
        stats={[
          {
            icon: <Gauge className="size-4" aria-hidden="true" />,
            label: 'Status',
            sub: 'Calibration',
            tone: status === 'valid' ? 'success' : status === 'overdue' || status === 'due_soon' ? 'accent' : 'neutral',
            loading: query.isLoading,
            value: instrument ? ndtCalibrationSkin(status).label : '—',
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Due',
            sub: daysHint(instrument?.daysUntilDue) ?? undefined,
            tone: 'neutral',
            loading: query.isLoading,
            value: ndtDay(instrument?.latestCalibration?.dueOn),
          },
          {
            icon: <CalendarCheck className="size-4" aria-hidden="true" />,
            label: 'Last cal',
            tone: 'neutral',
            loading: query.isLoading,
            value: ndtDay(instrument?.latestCalibration?.calibratedOn),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <NdtLoading label="Loading instrument" />
          ) : notFound || (!failure && !instrument) ? (
            <NdtPanel eyebrow="Instrument" title="Instrument">
              <NdtEmpty>This instrument was not found. Its NDT specs may have been detached.</NdtEmpty>
            </NdtPanel>
          ) : failure || !instrument ? (
            <NdtPanel eyebrow="Instrument" title="Instrument">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this instrument" />
            </NdtPanel>
          ) : (
            <>
              <NdtPanel
                eyebrow="Instrument"
                title="Specs"
                testId="ndt-specs-panel"
                actions={
                  canEdit ? (
                    <button type="button" className={NDT_SECONDARY} onClick={() => setEditing(true)}>
                      <Pencil className="size-4" aria-hidden="true" />
                      Edit specs
                    </button>
                  ) : undefined
                }
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <NdtSpec label="Kind" value={ndtKindLabel(instrument.specs?.instrumentKind)} />
                  <NdtSpec label="Serial number" value={instrument.serialNumber || '—'} mono />
                  <NdtSpec label="Manufacturer" value={instrument.manufacturer || '—'} />
                  <NdtSpec label="Model" value={instrument.model || '—'} />
                  <NdtSpec
                    label="Calibration interval"
                    value={instrument.specs?.calibrationIntervalDays != null ? `${instrument.specs.calibrationIntervalDays} days` : '—'}
                  />
                  <NdtSpec label="Equipment type" value={instrument.type || '—'} />
                  <NdtSpec label="Calibration" value={<NdtBadge skin={ndtCalibrationSkin(status)} value={status} />} />
                </dl>
                {instrument.specs?.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{instrument.specs.notes}</p>}
              </NdtPanel>

              <NdtPanel
                eyebrow="Append-only record"
                title="Calibration history"
                testId="ndt-calibration-history"
                footer={calibrations.length > 0 ? `${calibrations.length} calibration${calibrations.length === 1 ? '' : 's'}, newest first` : undefined}
              >
                {calibrations.length === 0 ? (
                  <NdtEmpty>No calibration has been logged for this instrument.</NdtEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">Calibrated</th>
                          <th scope="col" className="px-4 py-3">Due</th>
                          <th scope="col" className="px-4 py-3">Certificate #</th>
                          <th scope="col" className="px-4 py-3">Performed by</th>
                          <th scope="col" className="px-4 py-3">Notes</th>
                          <th scope="col" className="px-4 py-3">Entered by</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calibrations.map((c) => (
                          <tr key={c.id} className="border-t border-fieldiq-border">
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{ndtDay(c.calibratedOn)}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{ndtDay(c.dueOn)}</td>
                            <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{c.certificateNumber || '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{c.performedBy || '—'}</td>
                            <td className="max-w-[24rem] px-4 py-3 text-sm text-fieldiq-text">{c.notes || '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-muted">{c.createdByName || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </NdtPanel>

              {canEdit && (
                <div className="flex justify-end">
                  <button type="button" className={NDT_SECONDARY} onClick={() => { setDetachError(null); setDetaching(true); }}>
                    <Unlink className="size-4" aria-hidden="true" />
                    Detach from NDT
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {instrument && canEdit && <EditSpecsDialog open={editing} onClose={() => setEditing(false)} instrument={instrument} />}
      {instrument && canCalibrate && (
        <CalibrationDialog open={recording} onClose={() => setRecording(false)} instrument={instrument} today={meta?.today ?? null} />
      )}
      {instrument && canEdit && (
        <ConfirmDialog
          open={detaching}
          title="Detach instrument"
          body="Removes this unit from the NDT instrument register. The equipment unit and its calibration history stay."
          confirmLabel="Detach"
          pending={detach.isPending}
          error={detachError}
          onClose={() => setDetaching(false)}
          onConfirm={() =>
            detach.mutate(instrument.id, {
              onSuccess: () => {
                toast.success('Instrument detached');
                navigate('/ndt-inspection/instruments', { replace: true });
              },
              onError: (err) => setDetachError(ndtErrorMessage(err, 'The instrument could not be detached.')),
            })
          }
        />
      )}
    </div>
  );
}
